import * as XLSX from "xlsx";
import Papa from "papaparse";

// Recognizes roster-style imports (one row per person: a name, plus optional email/
// affiliation/judge/discipline columns) from .xlsx/.xls (via SheetJS) or .csv (via
// PapaParse), since organizers export this from all kinds of spreadsheets and form
// tools with no consistent header naming. classifyHeaders() produces a best-guess
// per-column mapping; the admin confirms/overrides it (RosterColumnMapping.js)
// before buildRosterSummary() actually reads the data, so an unusual layout gets a
// visible correction step instead of silently mis-importing.
const FIRST_NAME_RE = /^first\s*name$/i;
const LAST_NAME_RE = /^last\s*name$/i;
const FULL_NAME_RE = /^(full\s*name|name|fighter(\s*name)?|competitor(\s*name)?)$/i;
const EMAIL_RE = /^e-?mail(\s*address)?$/i;
const AFFILIATION_RE = /^(school|affiliation|club|team|organi[sz]ation|group)$/i;
const JUDGE_RE = /^judg(e|ing)$/i;
const DISCIPLINE_LIST_HEADER_RE = /^(discipline|disciplines|event|events|weapon|weapons)$/i;
const IGNORE_RES = [/t-?\s*shirt/i, /competitor\s*pass/i, /duelist\s*pass/i, /^weekend/i, /^notes?$/i];
const DISCIPLINE_LIST_SPLIT_RE = /[,;/|]+/;

// Falsy-blacklist rather than truthy-whitelist: any non-empty cell counts as "checked"
// unless it's one of a handful of explicit negative markers, so arbitrary conventions
// (checkmarks, "Enrolled", a discipline's own name typed in the cell, ...) work without
// hardcoding every possible affirmative mark.
const FALSY_VALUES = new Set(["", "false", "no", "n", "0", "-", "n/a", "na"]);
const isChecked = (v) => {
  if (v === true) return true;
  if (v === false || v === null || v === undefined) return false;
  return !FALSY_VALUES.has(v.toString().trim().toLowerCase());
};

const headerHasNameColumns = (headerRow) => {
  const norm = headerRow.map((h) => (h || "").toString().trim());
  const hasFirstLast = norm.some((h) => FIRST_NAME_RE.test(h)) && norm.some((h) => LAST_NAME_RE.test(h));
  const hasFullName = norm.some((h) => FULL_NAME_RE.test(h));
  return hasFirstLast || hasFullName;
};

// Scans the first few rows of a sheet (as arrays) for one that looks like a header row,
// so a title/logo row exported above the real header doesn't break detection.
const findHeaderRowIndex = (rowsAoA) => {
  const limit = Math.min(rowsAoA.length, 10);
  for (let i = 0; i < limit; i++) {
    if (headerHasNameColumns(rowsAoA[i] || [])) return i;
  }
  return -1;
};

// { rows: [{header: value}], headers: [header, ...] } regardless of source format.
export const parseRosterFile = async (file) => {
  if (file.name.toLowerCase().endsWith(".csv")) {
    const text = await file.text();
    // delimiter left unset (Papa's default is "" / auto-detect) so semicolon- or
    // tab-delimited exports parse correctly alongside comma-delimited ones.
    const { data, meta } = Papa.parse(text, { header: true, skipEmptyLines: true });
    return { rows: data, headers: meta.fields || [] };
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });

  let sheetName = workbook.SheetNames[0];
  let headerRowIndex = 0;
  for (const n of workbook.SheetNames) {
    const aoa = XLSX.utils.sheet_to_json(workbook.Sheets[n], { header: 1 });
    const idx = findHeaderRowIndex(aoa);
    if (idx !== -1) {
      sheetName = n;
      headerRowIndex = idx;
      break;
    }
  }

  const sheet = workbook.Sheets[sheetName];
  // Keep the header strings exactly as they appear in the file (not trimmed) — SheetJS
  // keys each row object by that same raw text, so a header with stray whitespace (the
  // source file has "School " with a trailing space) would otherwise mismatch between
  // the header list used to classify columns and the keys used to read row values.
  const headers = (XLSX.utils.sheet_to_json(sheet, { header: 1, range: headerRowIndex })[0] || [])
    .map((h) => (h || "").toString())
    .filter((h) => h.trim());
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", range: headerRowIndex });
  return { rows, headers };
};

// Best-guess default mapping from raw headers -> roles. Returned as an array (not an
// object) so it can be handed straight to RosterColumnMapping as editable state; the
// admin's confirmed version of this (possibly unchanged) is what buildRosterSummary
// actually uses. Any header that doesn't match a known role defaults to
// "disciplineCheckbox" — this is what lets an import handle whatever disciplines a
// given event runs without hardcoding names, while still being overridable if a
// leftover column isn't actually a discipline.
export const classifyHeaders = (headers) =>
  headers.map((h) => {
    const trimmed = h.trim();
    let role = "disciplineCheckbox";
    if (FIRST_NAME_RE.test(trimmed)) role = "firstName";
    else if (LAST_NAME_RE.test(trimmed)) role = "lastName";
    else if (EMAIL_RE.test(trimmed)) role = "email";
    else if (AFFILIATION_RE.test(trimmed)) role = "affiliation";
    else if (JUDGE_RE.test(trimmed)) role = "judge";
    else if (FULL_NAME_RE.test(trimmed)) role = "fullName";
    else if (DISCIPLINE_LIST_HEADER_RE.test(trimmed)) role = "disciplineList";
    else if (IGNORE_RES.some((re) => re.test(trimmed))) role = "ignore";
    return { header: h, role };
  });

const SINGULAR_ROLES = ["firstName", "lastName", "fullName", "email", "affiliation", "judge"];

// mapping: [{ header, role }] — role is one of firstName | lastName | fullName | email |
// affiliation | judge | disciplineCheckbox | disciplineList | ignore. Singular roles
// (SINGULAR_ROLES) resolve to at most one header each; disciplineCheckbox/disciplineList
// can apply to any number of headers.
export const buildRosterSummary = ({ rows, headers }, mapping) => {
  const roleToHeader = {};
  const checkboxKeys = [];
  const listKeys = [];
  for (const { header, role } of mapping) {
    if (role === "disciplineCheckbox") checkboxKeys.push(header);
    else if (role === "disciplineList") listKeys.push(header);
    else if (SINGULAR_ROLES.includes(role)) roleToHeader[role] = header;
  }
  const { firstName: firstNameKey, lastName: lastNameKey, fullName: fullNameKey, email: emailKey,
    affiliation: affiliationKey, judge: judgeKey } = roleToHeader;

  // discipline key -> { defaultName, sourceHeader, fighters: Set }. Checkbox columns
  // contribute one discipline each (key === header, the discipline IS the column).
  // List columns contribute one discipline per distinct token found in their cells
  // (key scoped to the column so the same word in two different list columns doesn't
  // collide) — sourceHeader is kept separately from defaultName so the preview step can
  // show which column produced each entry even though, for list-derived disciplines,
  // that's not the same as the discipline's own name.
  const disciplineData = new Map();
  for (const h of checkboxKeys) {
    disciplineData.set(h, { defaultName: h.trim(), sourceHeader: h, fighters: new Set() });
  }

  const judgeNames = new Set();
  const fighterEmails = {};
  const fighterAffiliations = {};
  let skippedRows = 0;

  for (const row of rows) {
    const name = fullNameKey
      ? (row[fullNameKey] || "").toString().trim()
      : [(row[firstNameKey] || "").toString().trim(), (row[lastNameKey] || "").toString().trim()]
          .filter(Boolean)
          .join(" ")
          .trim();
    if (!name) {
      skippedRows++;
      continue;
    }

    const email = emailKey ? (row[emailKey] || "").toString().trim() : "";
    if (email) fighterEmails[name] = email;

    const affiliation = affiliationKey ? (row[affiliationKey] || "").toString().trim() : "";
    if (affiliation) fighterAffiliations[name] = affiliation;

    if (judgeKey && isChecked(row[judgeKey])) judgeNames.add(name);

    for (const h of checkboxKeys) {
      if (isChecked(row[h])) disciplineData.get(h).fighters.add(name);
    }

    for (const h of listKeys) {
      const raw = (row[h] || "").toString();
      for (const token of raw.split(DISCIPLINE_LIST_SPLIT_RE).map((t) => t.trim()).filter(Boolean)) {
        const key = `list:${h}:${token.toLowerCase()}`;
        if (!disciplineData.has(key)) {
          disciplineData.set(key, { defaultName: token, sourceHeader: h, fighters: new Set() });
        }
        disciplineData.get(key).fighters.add(name);
      }
    }
  }

  const disciplines = [...disciplineData.entries()]
    .map(([key, d]) => ({ key, defaultName: d.defaultName, sourceHeader: d.sourceHeader, fighterNames: [...d.fighters].sort() }))
    .filter((d) => d.fighterNames.length > 0);

  return {
    disciplines,
    judgeNames: [...judgeNames].sort(),
    fighterEmails,
    fighterAffiliations,
    skippedRows,
    totalRows: rows.length,
  };
};

// selections: [{ key, disciplineName, include }]
export const resolveRosterDisciplines = (summary, selections) => {
  const rosters = {};
  for (const sel of selections) {
    if (!sel.include) continue;
    const name = (sel.disciplineName || "").trim();
    if (!name) continue;
    const discipline = summary.disciplines.find((d) => d.key === sel.key);
    rosters[name] = [...(discipline?.fighterNames || [])];
  }
  return rosters;
};

// Name-only extraction for seminar/training participant lists, which have no
// disciplines, emails, or judge flags — just people. Reuses classifyHeaders' name
// detection (First/Last or a single Name column). A sheet with no recognizable
// header is treated as a bare list in its first column, so the "header" cell is
// itself the first name rather than being dropped.
export const extractRosterNames = ({ rows, headers }) => {
  const mapping = classifyHeaders(headers);
  const keyFor = (role) => mapping.find((m) => m.role === role)?.header;
  const fullNameKey = keyFor("fullName");
  const firstNameKey = keyFor("firstName");
  const lastNameKey = keyFor("lastName");
  const cell = (row, key) => (key ? (row[key] ?? "").toString().trim() : "");

  let names;
  if (fullNameKey || firstNameKey || lastNameKey) {
    names = rows.map((row) => (fullNameKey
      ? cell(row, fullNameKey)
      : [cell(row, firstNameKey), cell(row, lastNameKey)].filter(Boolean).join(" ")));
  } else {
    const firstKey = headers[0];
    names = firstKey ? [firstKey.trim(), ...rows.map((row) => cell(row, firstKey))] : [];
  }
  return [...new Set(names.map((n) => n.replace(/\s+/g, " ").trim()).filter(Boolean))];
};
